import React, { useRef, useState } from 'react';
import { useStore } from '../store/useStore';
import { Download, Upload, Database, AlertCircle, CheckCircle } from 'lucide-react';

export function DataManagement() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const {
    categories, team, points, shifts, globalSettings,
    customFieldDefinitions, teamCustomFieldValues
  } = useStore();

  const handleExport = () => {
    try {
      const data = {
        categories,
        team,
        points,
        shifts,
        globalSettings,
        customFieldDefinitions,
        teamCustomFieldValues
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `we-shift-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccessMsg('Données exportées avec succès !');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setErrorMsg('Erreur lors de l\'exportation : ' + err.message);
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        
        // Basic validation
        if (!parsed.team || !parsed.points || !parsed.shifts) {
          throw new Error('Fichier de données invalide. Les données requises sont manquantes.');
        }

        useStore.setState((state) => ({
          ...state,
          categories: parsed.categories || state.categories,
          team: parsed.team || state.team,
          points: parsed.points || state.points,
          shifts: parsed.shifts || state.shifts,
          globalSettings: parsed.globalSettings || state.globalSettings,
          customFieldDefinitions: parsed.customFieldDefinitions || state.customFieldDefinitions,
          teamCustomFieldValues: parsed.teamCustomFieldValues || state.teamCustomFieldValues,
        }));

        setSuccessMsg('Données importées avec succès !');
        setTimeout(() => setSuccessMsg(null), 3000);
      } catch (err: any) {
        setErrorMsg('Erreur lors de l\'importation : ' + err.message);
        setTimeout(() => setErrorMsg(null), 5000);
      }
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="h-full flex flex-col items-center justify-center p-8 text-slate-300">
      <div className="max-w-2xl w-full bg-[#1A1D23] border border-[#2D3139] rounded-xl p-8 shadow-xl">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
            <Database className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Gestion des Données</h2>
            <p className="text-slate-400">Importez et exportez vos configurations (team, points, shifts, etc.)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {/* Export Card */}
          <div className="bg-[#0A0B0E] p-6 rounded-lg border border-[#2D3139] hover:border-indigo-500/50 transition-colors group">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
              <Download className="w-5 h-5 mr-2 text-indigo-400" />
              Exporter
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Téléchargez toutes vos données actuelles sous forme de fichier JSON. Vous pourrez utiliser ce fichier pour restaurer ou migrer.
            </p>
            <button
              onClick={handleExport}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg transition-colors font-medium"
            >
              Sauvegarder les données
            </button>
          </div>

          {/* Import Card */}
          <div className="bg-[#0A0B0E] p-6 rounded-lg border border-[#2D3139] hover:border-emerald-500/50 transition-colors group">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
              <Upload className="w-5 h-5 mr-2 text-emerald-400" />
              Importer
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Restaurez vos données à partir d'un fichier JSON. <strong className="text-red-400">Attention:</strong> cela remplacera vos données actuelles.
            </p>
            <input 
              type="file" 
              accept=".json" 
              className="hidden" 
              ref={fileInputRef}
              onChange={handleImport}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg transition-colors font-medium"
            >
              Charger un fichier
            </button>
          </div>
        </div>

        {/* Notifications */}
        {successMsg && (
          <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center text-emerald-400">
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <p className="font-medium text-sm">{successMsg}</p>
          </div>
        )}
        
        {errorMsg && (
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center text-red-400">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <p className="font-medium text-sm">{errorMsg}</p>
          </div>
        )}
      </div>
    </div>
  );
}
